import React, { FC } from 'react';
import { useNavigate } from 'react-router';
import RadarChartComp from '../RadarChart/RadarChartComp';
import { ICompetenciesBlock } from 'core/interface/competenciesBlock';
import ChartComponent from 'components/RadarChart/Index';
import { ChartData } from 'chart.js';
import Button from 'components/Button/Index';
import useLocales from 'hooks/useLocales';
import ChartDescription, { ChartPoint } from './ChartDescription';

import './Index.scss';

const CompetenciesBlock: FC<ICompetenciesBlock> = ({
  title,
  percent,
  className,
  labels,
  points,
  descriptions = null,
  chartWidth,
  chartHeight,
  chartData,
  careerPage = false,
  job,
  candidateId,
  noLabels,
  showDescription = true,
  comparePoints,
  hideHeader = false
}): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();

  // Process data for chart description
  const getChartPoints = (): ChartPoint[] => {
    let chartPoints: ChartPoint[] = [];

    // Case 1: We have direct labels and points
    if (labels && points && labels.length > 0 && points.length > 0) {
      chartPoints = labels.map((label, index) => {
        // Get comparison point if available
        let comparisonPoint = undefined;
        if (comparePoints && comparePoints.length > 0) {
          comparisonPoint =
            comparePoints[index]?.score ||
            comparePoints[index]?.value ||
            comparePoints[index];
        } else if (chartData?.datasets && chartData.datasets.length > 1) {
          comparisonPoint = chartData.datasets[1].data[index];
        }

        return {
          point: points[index] || 0,
          label,
          descriptions:
            Array.isArray(descriptions) && descriptions[index]
              ? typeof descriptions[index] === 'string'
                ? (descriptions[index] as string)
                : (descriptions[index] as any).description ||
                  (descriptions[index] as any).name ||
                  ''
              : undefined,
          comparePoint: comparisonPoint
        };
      });
    }
    // Case 2: We have chartData
    else if (
      chartData &&
      chartData.labels &&
      chartData.datasets &&
      chartData.datasets.length > 0
    ) {
      const dataLabels = chartData.labels as string[];
      const dataPoints = chartData.datasets[0].data as number[];

      chartPoints = dataLabels.map((label, index) => {
        // Get comparison point if available
        let comparisonPoint = undefined;
        if (comparePoints && comparePoints.length > 0) {
          comparisonPoint =
            comparePoints[index]?.score ||
            comparePoints[index]?.value ||
            comparePoints[index];
        } else if (chartData.datasets.length > 1) {
          comparisonPoint = chartData.datasets[1].data[index];
        }

        return {
          point: dataPoints[index] || 0,
          label: typeof label === 'string' ? label : `Item ${index + 1}`,
          descriptions:
            Array.isArray(descriptions) && descriptions[index]
              ? typeof descriptions[index] === 'string'
                ? (descriptions[index] as string)
                : (descriptions[index] as any).description ||
                  (descriptions[index] as any).name ||
                  ''
              : undefined,
          comparePoint: comparisonPoint
        };
      });
    }

    return chartPoints;
  };

  // Check if we have enough data to show the chart description
  const hasValidData = () => {
    // Check if we have direct labels and points
    const hasDirectData =
      labels && points && labels.length > 0 && points.length > 0;

    // Check if we have chart data
    const hasChartData =
      chartData &&
      chartData.labels &&
      chartData.datasets &&
      chartData.datasets.length > 0 &&
      chartData.datasets[0].data;

    return hasDirectData || hasChartData;
  };

  return (
    <div className={`competenciesb ${className ?? ''}`}>
      <div className="competenciesb__top">
        <div
          className="competenciesb__top__left"
          style={{ alignItems: 'start' }}
        >
          <span
            className="competenciesb__top__left-title"
            style={{ color: 'black' }}
          >
            {title}
          </span>
          {percent !== undefined && percent !== null && (
            <span className="competenciesb__top__left-percent">{percent}%</span>
          )}
        </div>
        {!noLabels && (
          <div className="competenciesb__top__right">
            <div className="competenciesb__top__right__item">
              <span className="competenciesb__top__right__item-dot purple"></span>
              <span className="competenciesb__top__right__item-title">
                {t('Job')}
              </span>
            </div>
            <div className="competenciesb__top__right__item">
              <span className="competenciesb__top__right__item-dot"></span>
              <span className="competenciesb__top__right__item-title">
                {t('Candidate')}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="competenciesb__chart">
        {labels && points && (
          <RadarChartComp
            title={t('Match job competencies')}
            labels={labels}
            points={points}
            comparePoints={comparePoints}
            descriptions={descriptions}
            chartWidth={chartWidth}
            chartHeight={chartHeight}
          />
        )}
        {chartData && (
          <ChartComponent
            width={chartWidth}
            height={chartHeight}
            data={chartData as ChartData}
            showTicks={false}
          />
        )}
      </div>

      {/* Chart Description Section */}
      {showDescription && descriptions && hasValidData() && (
        <ChartDescription labels={getChartPoints()} hideHeader={hideHeader} />
      )}

      {/* {careerPage && (
        <Button
          onClick={() => {
            navigate(`/company/career/${job}/${candidateId}`);
          }}
          t={t}
          className="career_page"
          title={t('View our career page')}
        />
      )} */}
    </div>
  );
};

export default CompetenciesBlock;
